import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { SEOHead } from '@/components/SEO/SEOHead';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Linkedin, Twitter, Mail, ExternalLink } from 'lucide-react';

export function AboutPage() {
  const { t } = useTranslation('about');
  const { locale } = useParams();
  const currentLocale = locale || 'fr';

  // Schema.org Person
  const personSchema = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: 'Corentin Leroux',
    jobTitle: t('schema.jobTitle'),
    description: t('schema.description'),
    url: 'https://aspexit.com',
    image: 'https://aspexit.com/corentin-leroux.jpg',
    sameAs: [
      'https://www.linkedin.com/in/corentinleroux/',
      'https://twitter.com/CP_Leroux',
      'https://corentinleroux.substack.com',
    ],
    worksFor: {
      '@type': 'Organization',
      name: 'Aspexit',
      url: 'https://aspexit.com',
    },
    knowsAbout: [
      t('schema.expertise.1'),
      t('schema.expertise.2'),
      t('schema.expertise.3'),
      t('schema.expertise.4'),
    ],
    alumniOf: {
      '@type': 'Organization',
      name: t('schema.education'),
    },
  };

  return (
    <>
      <SEOHead
        title={t('meta.title')}
        description={t('meta.description')}
        path="/about"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(personSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-aspexit-green/5 via-white to-aspexit-blue/5">
        {/* Hero Section */}
        <section className="py-16 bg-gradient-to-r from-aspexit-green to-aspexit-green-dark text-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="flex flex-col md:flex-row gap-8 items-center">
              {/* Photo (placeholder) */}
              <div className="w-48 h-48 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                <div className="text-6xl">👨‍🌾</div>
              </div>

              {/* Intro */}
              <div className="flex-1 text-center md:text-left">
                <h1 className="text-4xl md:text-5xl font-heading font-extrabold mb-4">
                  {t('hero.title')}
                </h1>
                <p className="text-xl text-white/90 mb-6">
                  {t('hero.subtitle')}
                </p>

                {/* Social Links */}
                <div className="flex gap-4 justify-center md:justify-start">
                  <Button
                    asChild
                    variant="outline"
                    size="sm"
                    className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm"
                  >
                    <a
                      href="https://www.linkedin.com/in/corentinleroux/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Linkedin className="w-4 h-4 mr-2" />
                      LinkedIn
                    </a>
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    size="sm"
                    className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm"
                  >
                    <a
                      href="https://twitter.com/CP_Leroux"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Twitter className="w-4 h-4 mr-2" />
                      Twitter
                    </a>
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    size="sm"
                    className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm"
                  >
                    <a
                      href="https://corentinleroux.substack.com"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Newsletter
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Bio Section */}
        <section className="py-12">
          <div className="container mx-auto px-4 max-w-4xl">
            <Card className="p-8 mb-8">
              <h2 className="text-3xl font-heading font-bold text-aspexit-black mb-6">
                {t('bio.title')}
              </h2>

              <div className="prose prose-lg max-w-none space-y-4 text-aspexit-gray">
                <p>{t('bio.paragraph1')}</p>
                <p>{t('bio.paragraph2')}</p>
                <p>{t('bio.paragraph3')}</p>
              </div>
            </Card>

            {/* Expertise */}
            <Card className="p-8 mb-8 bg-gradient-to-br from-aspexit-green/5 to-white">
              <h2 className="text-3xl font-heading font-bold text-aspexit-black mb-6">
                {t('expertise.title')}
              </h2>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-aspexit-black mb-2 flex items-center gap-2">
                    <span className="text-2xl">📊</span>
                    {t('expertise.item1.title')}
                  </h3>
                  <p className="text-aspexit-gray text-sm">
                    {t('expertise.item1.description')}
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-aspexit-black mb-2 flex items-center gap-2">
                    <span className="text-2xl">🌾</span>
                    {t('expertise.item2.title')}
                  </h3>
                  <p className="text-aspexit-gray text-sm">
                    {t('expertise.item2.description')}
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-aspexit-black mb-2 flex items-center gap-2">
                    <span className="text-2xl">🎓</span>
                    {t('expertise.item3.title')}
                  </h3>
                  <p className="text-aspexit-gray text-sm">
                    {t('expertise.item3.description')}
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-aspexit-black mb-2 flex items-center gap-2">
                    <span className="text-2xl">✍️</span>
                    {t('expertise.item4.title')}
                  </h3>
                  <p className="text-aspexit-gray text-sm">
                    {t('expertise.item4.description')}
                  </p>
                </div>
              </div>
            </Card>

            {/* CTA Contact */}
            <Card className="p-8 bg-gradient-to-r from-aspexit-green to-aspexit-green-dark text-white text-center">
              <h2 className="text-2xl font-heading font-bold mb-4">
                {t('cta.title')}
              </h2>
              <p className="text-white/90 mb-6">{t('cta.description')}</p>
              <Button
                asChild
                size="lg"
                className="bg-white text-aspexit-black hover:bg-gray-100 font-semibold"
              >
                <a href={`/${currentLocale}/contact`}>
                  <Mail className="w-5 h-5 mr-2" />
                  {t('cta.button')}
                </a>
              </Button>
            </Card>
          </div>
        </section>
      </div>
    </>
  );
}
